/**
 * facetTaxonomy — single source of truth for the directory facet option
 * lists (payment types, features, delivery / home-care / respite models,
 * medicaid, gender restrictions).
 *
 * Before this module the same lists were duplicated across FilterBar.jsx
 * (directory filters), useUnifiedSearch.js (filter dispatch),
 * OrganizationDetail.jsx (the edit page), and SuggestResourceForm.jsx — a
 * drift surface where the suggest form, the filters, and the provider schema
 * could fall out of step. Every consumer now imports from here.
 *
 * The display values match the catalog tables EXACTLY so a suggested-resource
 * selection resolves at approve time:
 *   - MEDICAID_VALUES / MEMORY_CARE_CATALOG_VALUE / GENDER_RESTRICTION_VALUES
 *     → provider_highlights.display_name (families medicaid / memory_care /
 *       gender_restrictions; migration 0069)
 *   - SERVICE_DELIVERY_MODELS → provider_service_models.display_name
 *     (migration 0069 + 0088: Mobile dropped, Live-in added)
 *   - PAYMENT_TYPES → provider_organizations.payment_types jsonb (migration 0058)
 *
 * Server-side mirror: api/_shared/schema.ts (PAYMENT_TYPES / MEDICAID_VALUES /
 * SERVICE_DELIVERY_MODELS / GENDER_RESTRICTIONS). The api/ and src/ boundaries
 * cannot share a module, so that copy is kept in sync by hand; this is the
 * canonical src-side source.
 */

/** provider_organizations.payment_types jsonb vocabulary. */
export const PAYMENT_TYPES = [
  'Medicaid',
  'Medicare',
  'Private Pay',
  'Insurance',
  'VA',
  'Sliding Scale',
] as const;

/** provider_highlights family='medicaid' display_names. Migration 0129
 *  rewrote the spaced-hyphen " - " to the house " · " separator in the
 *  catalog AND pending suggestions — labels and values move together
 *  (the facet unification ruling; the spaced-hyphen lint unlock). */
export const MEDICAID_VALUES = [
  'Yes · Straight',
  'Yes · Spend Down with Guarantee',
  'Yes · Spend Down NO Guarantee',
  'Yes · Type TBD',
  'No',
] as const;

/** provider_highlights family='gender_restrictions' display_names. */
export const GENDER_RESTRICTION_VALUES = ['Women Only', 'Men Only'] as const;
/** Alias under the directory-filter name (useUnifiedSearch / FilterBar). */
export const RESTRICTIONS_LABELS = GENDER_RESTRICTION_VALUES;

/** provider_highlights family='memory_care' catalog display_name. */
export const MEMORY_CARE_CATALOG_VALUE = 'Secure Dementia Unit';
/** Directory-filter label that routes to the memory_care assignment. */
export const MEMORY_CARE_LABEL = 'Has Secure Dementia Unit';

/**
 * provider_service_models display_names, partitioned for presentation (E3 /
 * migration 0088). The directory "Delivery Model" pill shows the location
 * values; Home Care Type + Respite surface under the Features pill. All are
 * the same underlying service_delivery_models[] vocabulary.
 */
export const DELIVERY_MODEL_LOCATIONS = [
  'In-Facility',
  'Outpatient',
  'In-Home',
  'House Calls',
  'Telehealth',
] as const;
export const HOME_CARE_TYPE_LABELS = ['Hourly', '24/7', 'Live-in'] as const;
export const RESPITE_MODEL_VALUE = 'Respite';
export const RESPITE_DISPLAY_LABEL = 'Offers respite';

/** Every active provider_service_models.display_name (the 9 post-0088 values). */
export const SERVICE_DELIVERY_MODELS = [
  ...DELIVERY_MODEL_LOCATIONS,
  ...HOME_CARE_TYPE_LABELS,
  RESPITE_MODEL_VALUE,
] as const;

/**
 * Grouped Features picker structure for the directory filter. Sub-item
 * labels are unique across families so a flat string[] selection suffices;
 * useUnifiedSearch dispatches each label to the right column at filter time.
 */
export const FEATURES_FILTER_GROUPS = [
  { family: 'Medicaid', items: [...MEDICAID_VALUES] },
  { family: 'Memory Care', items: [MEMORY_CARE_LABEL] },
  { family: 'Gender Restrictions', items: [...GENDER_RESTRICTION_VALUES] },
  { family: 'Home Care Type', items: [...HOME_CARE_TYPE_LABELS] },
  { family: 'Respite', items: [RESPITE_DISPLAY_LABEL] },
] as const;

/** {value,label} shape for EditableSelect / EditableMultiSelect consumers. */
export function asOptions(values: readonly string[]): Array<{ value: string; label: string }> {
  return values.map((v) => ({ value: v, label: v }));
}
